import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import {
  Button,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@scio3/ui';
import { SHARE_LINK_ACCESS_ROLES } from '@scio3/shared-types';
import type { ShareLink } from '@scio3/shared-types';

type Props = {
  shareLink: ShareLink;
  onUpdatePeopleRole: (_id: string, userId: string, role: string) => Promise<void>;
};

export function ShareLinkPeopleTable({ shareLink, onUpdatePeopleRole }: Props) {
  const { t } = useTranslation();

  const handleRoleChange = useCallback(
    async (userId: string, role: string) => {
      await onUpdatePeopleRole(shareLink._id, userId, role);
    },
    [shareLink._id, onUpdatePeopleRole],
  );

  const handleRemove = useCallback(
    async (userId: string) => {
      await onUpdatePeopleRole(shareLink._id, userId, 'removeAccess');
    },
    [shareLink._id, onUpdatePeopleRole],
  );

  const securities = shareLink.securities ?? [];

  return (
    <div className="space-y-2">
      <Label>{t('People with Access')}</Label>
      <div className="space-y-1">
        {securities.map((user) => (
          <div
            key={user.userId}
            className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-accent/50 transition-colors"
          >
            <span className="flex-1 truncate text-sm">{user.userId}</span>
            <Select
              value={user.role}
              onValueChange={(value) => handleRoleChange(user.userId, value)}
            >
              <SelectTrigger className="w-24 h-8 cursor-pointer">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={SHARE_LINK_ACCESS_ROLES.EDITOR}>{t('Editor')}</SelectItem>
                <SelectItem value={SHARE_LINK_ACCESS_ROLES.VIEWER}>{t('Viewer')}</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={() => handleRemove(user.userId)}
              aria-label={t('Remove access')}
              className="cursor-pointer text-muted-foreground hover:text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
